import { createRoute, useParams } from '@tanstack/react-router';
import { Action, Subject } from '@erp/authz';
import { orpc } from '@/api/rpc/client';
import {
  Route as pathlessAuthenticatedRoute,
  requirePermission,
} from '../../route';
import { TaskReviewView } from './TaskReviewView';
import { buildTaskDetailCrumbs, tasksHomePath } from './taskDetailCrumbs';
import { isBooleanFeatureFlagEnabled } from '@/integrations/feature-flags/feature-flags';
import { taskDetailSearchSchema } from '../taskDetailNav';

/**
 * Full-page task detail — the review shell for one `core_tasks` row (header,
 * derived-phase stepper, subtask tree, unified audit timeline, anchor entity
 * preview, declared type actions). Renders directly into the authenticated
 * layout's height-bounded `<main>` so the columns scroll independently.
 */
/** A failed lookup degrades to a fallback crumb rather than failing the route. */
async function settle<T>(promise: Promise<T>): Promise<T | null> {
  try {
    return await promise;
  } catch {
    return null;
  }
}

function TaskDetailPage() {
  const { taskId } = useParams({ from: '/authenticated/tasks/$taskId' });
  return <TaskReviewView taskId={taskId} />;
}

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/tasks/$taskId',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Task }],
      },
    },
  },
  component: TaskDetailPage,
  validateSearch: taskDetailSearchSchema,
  loaderDeps: ({ search }) => ({
    fromPage: search.fromPage,
    // A task page tab pins its type into the queue search the row link carries.
    fromPageTab: search.list === 'queue' ? search.type : undefined,
  }),
  loader: async ({ params, context, deps }) => {
    const { fromPage, fromPageTab } = deps;
    const fetchTask = (id: string) =>
      context.queryClient.fetchQuery(
        orpc.tasks.get.queryOptions({ input: { id } }),
      );

    const task = await settle(fetchTask(params.taskId));
    const parentTaskId = task?.parentTaskId ?? null;
    const [page, parent] = await Promise.all([
      fromPage
        ? settle(
            context.queryClient.fetchQuery(
              orpc.taskPages.get.queryOptions({ input: { id: fromPage } }),
            ),
          )
        : null,
      parentTaskId ? settle(fetchTask(parentTaskId)) : null,
    ]);

    return buildTaskDetailCrumbs({
      tasksPath: tasksHomePath({
        tasksEnabled: isBooleanFeatureFlagEnabled('tasks-enabled', false),
        inboxEnabled: isBooleanFeatureFlagEnabled('inbox-enabled', false),
      }),
      fromPage,
      fromPageTab,
      task,
      page,
      parentTaskId,
      parent,
    });
  },
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Task,
    });
  },
});
